import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@els/els-react--loader';
import { imageTypes } from '@Constants/rx-ui/constants';
import { getCachedImageUrlIfAvailable, getCachedReactionImageUrl } from './conditionsReactionImageCache';

function ConditionsReactionImage({
  reaction,
  imageType = imageTypes.CONDITIONS_NAVIGATOR_L,
  className,
  alt,
  loadingFallback = <Loader isVisible isBlocking={false} />,
  emptyFallback = null,
}) {
  const [imageUrl, setImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const shouldShowLoading = loadingFallback != null;
  const imageTypeModifier =
    imageType === imageTypes.CONDITIONS_NAVIGATOR_S ? 'conditions-reaction-image--small' : 'conditions-reaction-image--large';
  const wrapperClassName = ['conditions-reaction-image', imageTypeModifier, className].filter(Boolean).join(' ');

  useEffect(() => {
    let isMounted = true;
    setImageUrl('');
    setHasFailed(false);

    if (!reaction) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const { isCached, imageUrl: cachedImageUrl } = getCachedImageUrlIfAvailable(reaction, imageType);

    if (isCached) {
      setImageUrl(cachedImageUrl);
      setHasFailed(false);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (shouldShowLoading) setIsLoading(true);
    getCachedReactionImageUrl(reaction, imageType)
      .then((renderedImageUrl) => {
        if (!isMounted) return;
        setImageUrl(renderedImageUrl);
        setHasFailed(!renderedImageUrl);
      })
      .catch((error) => {
        if (!isMounted) return;
        setHasFailed(true);
        Logger.error('ConditionsReactionImage render failed with error:', error);
      })
      .finally(() => {
        if (isMounted && shouldShowLoading) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [imageType, reaction, shouldShowLoading]);

  if (!reaction) return null;

  if (isLoading && shouldShowLoading) {
    return <span className={wrapperClassName}>{loadingFallback}</span>;
  }

  if (hasFailed && !imageUrl) {
    return <span className={wrapperClassName}>{emptyFallback}</span>;
  }

  if (!imageUrl) return <span className={wrapperClassName} />;

  return (
    <span className={wrapperClassName}>
      <img className="conditions-reaction-image__img" src={imageUrl} alt={alt} />
    </span>
  );
}

ConditionsReactionImage.propTypes = {
  reaction: PropTypes.string,
  imageType: PropTypes.oneOf([imageTypes.CONDITIONS_NAVIGATOR_S, imageTypes.CONDITIONS_NAVIGATOR_L]),
  className: PropTypes.string,
  alt: PropTypes.string.isRequired,
  loadingFallback: PropTypes.node,
  emptyFallback: PropTypes.node,
};

export default ConditionsReactionImage;
